import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import presets from 'config/package-search.json';
import { SolidButton } from 'components/Button';
import FieldTrigger, {
  FieldVariant,
  fieldWrapperClassName,
  panelPositionClassName,
} from './FieldTrigger';

function formatPrice(value: number, locale?: string): string {
  // Group digits the way the page's language does: 1,500 € / 1 500 €.
  return new Intl.NumberFormat(locale === 'hu' ? 'hu-HU' : 'en-GB', {
    style: 'currency',
    currency: presets.price.currency,
    maximumFractionDigits: 0,
  }).format(value);
}

interface IPriceDropdownProps {
  // Whole currency units per person; null = no limit (the slider's top).
  value: number | null;
  onChange: (v: number | null) => void;
  variant?: FieldVariant;
}

const PriceDropdown: React.FC<IPriceDropdownProps> = ({
  value,
  onChange,
  variant = 'field',
}) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { min, max, step } = presets.price;
  const position = value ?? max;
  const label =
    value === null
      ? t('Any price')
      : t('up to {{price}}', { price: formatPrice(value, i18n.language) });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={fieldWrapperClassName(variant)}>
      <FieldTrigger
        label={t('Price /person')}
        showLabel
        variant={variant}
        open={open}
        filled={value !== null}
        onClick={() => setOpen(o => !o)}>
        <span className="text-black">{label}</span>
      </FieldTrigger>

      {open && (
        <div
          className={`absolute z-50 top-full mt-1 ${panelPositionClassName(
            variant,
          )} min-w-[280px] bg-white rounded-xl shadow-lg border border-tt-gray-purple`}>
          <div className="p-4 space-y-3">
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={position}
              // WHIT-526: named for screen readers; at the far end the value
              // is "any price", not a limit.
              aria-label={t('Price /person')}
              aria-valuetext={label}
              onChange={e => {
                const next = Number(e.target.value);
                onChange(next >= max ? null : next);
              }}
              className="w-full accent-tt-yellow"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{formatPrice(min, i18n.language)}</span>
              <span className="font-semibold text-tt-dark-purple">{label}</span>
              <span>{formatPrice(max, i18n.language)}</span>
            </div>
            <div className="grid grid-cols-2 gap-1 pt-1 border-t border-tt-gray-purple">
              {presets.price.presets.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onChange(p)}
                  className={`text-xs px-2 py-1.5 rounded-lg border transition-colors ${
                    value === p
                      ? 'bg-tt-yellow border-tt-dark-yellow font-semibold'
                      : 'border-tt-light-purple hover:bg-tt-verylight-purple'
                  }`}
                >
                  {t('up to {{price}}', {
                    price: formatPrice(p, i18n.language),
                  })}
                </button>
              ))}
            </div>
          </div>
          <div className="p-2 border-t border-tt-gray-purple">
            <SolidButton
              onClick={() => setOpen(false)}
              className="w-full !py-2.5"
            >
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceDropdown;
